/**
 * Canonical list of job nav menu items that can be shown/hidden per business category.
 *
 * This module is intentionally free of React / tRPC / client-only imports so it can be shared by
 * both the client nav builder (`useJobNavItemsForRole`) and the server-side validation schema
 * (`BusinessSchema`). The `id`s must stay in sync with the items built in `useJobNavItemsForRole`;
 * the per-category settings UI renders a checkbox for each entry here (except entries flagged
 * `hideFromCategorySettings`, whose visibility is driven by the job itself rather than category config).
 */
export const JOB_NAV_ITEM_OPTIONS = [
	{ id: 'job-nav-overview', name: 'Overview' },
	{ id: 'job-nav-attachments', name: 'Attachments' },
	{ id: 'job-nav-proposal-builder', name: 'Proposal Builder' },
	// Bid Studio visibility is driven by the business's `enablePlanAnalysis` flag, not per-category config.
	{ hideFromCategorySettings: true, id: 'job-nav-bid-studio', name: 'Bid Studio' },
	{ id: 'job-nav-estimates', name: 'Estimates' },
	{ id: 'job-nav-change-orders', name: 'Change Orders' },
	{ id: 'job-nav-submittals', name: 'Submittals' },
	{ id: 'job-nav-glass-builder', name: 'Glass Builder' },
	{ id: 'job-nav-work-orders', name: 'Work Orders' },
	{ id: 'job-nav-floor-plans', name: 'Floor Plan' },
	{ id: 'job-nav-measures-and-installs', name: 'Measures & Installs' },
	{ id: 'job-nav-daily-reports', name: 'Daily Reports' },
	{ id: 'job-nav-quote-requests', name: 'Quote Requests' },
	{ id: 'job-nav-purchase-orders', name: 'Purchase Orders' },
	{ id: 'job-nav-phases', name: 'Phases' },
	{ id: 'job-nav-key-dates', name: 'Job Tasks' },
	{ id: 'job-nav-planning', name: 'Gantt Chart' },
	{ id: 'job-nav-materials-equipment', name: 'Materials and Equipment' },
	{ id: 'job-nav-payments', name: 'Payments' },
	{ id: 'job-nav-job-costing', name: 'Job Costing' },
	{ id: 'job-nav-job-sync-log', name: 'Accounting Sync' },
	{ id: 'job-nav-smart-takeoff', name: 'Smart Takeoff' },
	// Progress Billing visibility is driven by the job's `isCommercial` flag, not per-category config,
	// so it is excluded from the business category settings UI.
	{ hideFromCategorySettings: true, id: 'job-nav-commercial-billing', name: 'Progress Billing' },
] as const satisfies readonly { hideFromCategorySettings?: boolean; id: string; name: string }[];

export type JobNavItemOption = (typeof JOB_NAV_ITEM_OPTIONS)[number];

export type JobNavItemId = JobNavItemOption['id'];

export const JOB_NAV_ITEM_IDS: readonly JobNavItemId[] = JOB_NAV_ITEM_OPTIONS.map((item) => item.id);

/** Nav items configurable via the per-business-category settings UI (excludes job-driven items). */
export const CATEGORY_CONFIGURABLE_JOB_NAV_ITEM_OPTIONS = JOB_NAV_ITEM_OPTIONS.filter(
	(item) => !('hideFromCategorySettings' in item && item.hideFromCategorySettings)
);
